/**
 * Mobile Position Assignment Modal
 * Professional mobile version matching desktop quality
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { needsActivateWhenFull, eligibleSlotMates } from '../../../services/activateWhenFull';
import { formatApiError } from '../../../services/errorUtils';
import {
  X, Activity, Users, Heart, Shield, CheckCircle, AlertTriangle,
  Crown, ChevronDown, ArrowLeftRight
} from 'lucide-react';
import { dynastyTheme, withAlpha } from '../../../services/colorService';
// Layer 1 of the 6-layer eligibility invariant (Tony 2026-05-13).
// Shared helper reads canonical roster.effective_eligibility (populated by
// player_service._inject_effective_eligibility, which composes default +
// auto-earned-from-current-or-prior-season + custom_override). NEVER
// re-implement composite-slot expansion in the frontend.
import { eligibleSlotTypes, getEffectiveEligibility } from '../../../utils/positionEligibility';

const C = dynastyTheme.tokens.colors;

const MobilePositionAssignmentModal = ({
  player,
  league,
  currentRoster,
  onAssign,
  onCancel,
  isVisible = false,
  mode = 'move',
  currentStatus = null,
  currentPosition = null,
  // Phase 2.3: drop-shuffle entry point. Parent owns
  // MobileRosterPlanModal lifecycle. Per § B.5.1 + § B.5.7.
  // Renders when (active full AND bench/DL/minors has room).
  onOpenDropShuffle = null
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || 'active');
  const [selectedOption, setSelectedOption] = useState('');
  const [error, setError] = useState('');
  // DD-DL-DEADLOCK-0726 — activate-when-full (§14.0 parity with PositionAssignmentDropdown).
  const [displaceChoice, setDisplaceChoice] = useState(null);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  // User-intent ref (Bug 1 fix, 2026-05-15). Flips to true on any explicit
  // status-button click (ACTIVE/BENCH/DL/MINORS) or position-row click. The
  // auto-select useEffect early-returns when this is true so that clicking
  // ACTIVE with no available active slots doesn't silently revert to bench.
  // Reset on modal close so the next open auto-selects fresh.
  const userPickedSlotRef = useRef(false);

  // Reset status when modal opens
  useEffect(() => {
    if (isVisible && mode === 'move' && currentStatus) {
      setSelectedStatus(currentStatus);
    }
  }, [isVisible, mode, currentStatus]);

  // Analyze position availability
  const analyzePositionAvailability = useMemo(() => {
    if (!league?.roster_settings?.position_requirements || !Array.isArray(currentRoster)) {
      return { available: [], alternatives: [], benchOption: null, minorsOption: null };
    }

    // Canonical eligibility — single source of truth (Bible § 4.5.9).
    // No fallback to [primary_position] alone — that would skip composite-slot
    // expansion and auto-earned-from-prior-season carry-over, both of which
    // the backend's compute_effective_eligibility already handles. If the
    // canonical field is missing, the player has no eligible slots in the UI
    // (defensive; surfaces missing data rather than silently degrading).
    const eligibleTypes = eligibleSlotTypes(player);

    const positionRequirements = league.roster_settings.position_requirements;
    const benchSlots = league.roster_settings?.bench_slots || 0;
    const dlSlots = league.roster_settings?.dl_slots || 0;
    const minorSlots = league.roster_settings?.minor_league_slots || 0;

    // Count current assignments
    const activeAssignments = {};
    const occupiedSlots = new Set();
    
    currentRoster.forEach(rosterPlayer => {
      const status = rosterPlayer.league_context?.roster?.status;
      const position = rosterPlayer.league_context?.roster?.position;
      if (status === 'active' && position) {
        const positionBase = position.split('_')[0];
        activeAssignments[positionBase] = (activeAssignments[positionBase] || 0) + 1;
        occupiedSlots.add(position);
      }
    });

    const benchCount = currentRoster.filter(p => p.league_context?.roster?.status === 'bench').length;
    const dlCount = currentRoster.filter(p => p.league_context?.roster?.status === 'dl').length;
    const minorsCount = currentRoster.filter(p => p.league_context?.roster?.status === 'minors').length;

    // Composite-slot expansion lives in the backend canonical helper. Here we
    // just check membership in the pre-composed effective set. NEVER re-implement
    // MI/CI/UTIL/P/OF special cases locally — that was the e142ad4c (April 24)
    // "Fifth attempt" bug class, the Wetherholt (May 13) bypass class, and any
    // future drift class. One source of truth.

    const available = [];
    const alternatives = [];

    Object.entries(positionRequirements).forEach(([position, config]) => {
      const maxSlots = config?.slots || 0;
      const currentlyFilled = activeAssignments[position] || 0;
      const availableSlots = maxSlots - currentlyFilled;

      if (eligibleTypes.has(position)) {
        let vacantSlotId = null;
        if (availableSlots > 0) {
          for (let i = 0; i < maxSlots; i++) {
            const potentialSlotId = `${position}_${i}`;
            if (!occupiedSlots.has(potentialSlotId)) {
              vacantSlotId = potentialSlotId;
              break;
            }
          }
        }
        
        const option = {
          position,
          availableSlots,
          maxSlots,
          currentlyFilled,
          canAssign: availableSlots > 0,
          slotId: vacantSlotId
        };
        
        if (availableSlots > 0) {
          available.push(option);
        } else {
          alternatives.push(option);
        }
      }
    });

    const benchOption = benchSlots > 0 ? {
      position: 'bench',
      availableSlots: benchSlots - benchCount,
      maxSlots: benchSlots,
      currentlyFilled: benchCount,
      canAssign: benchCount < benchSlots,
      slotId: 'bench'
    } : null;

    const minorsOption = minorSlots > 0 ? {
      position: 'minors',
      availableSlots: minorSlots - minorsCount,
      maxSlots: minorSlots,
      currentlyFilled: minorsCount,
      canAssign: minorsCount < minorSlots,
      slotId: 'minors'
    } : null;

    return { available, alternatives, benchOption, minorsOption, activeAssignments };
  }, [league, currentRoster, player]);

  // Auto-select best position. Bug 1 fix (2026-05-15): early-return when the
  // user has explicitly picked anything in this modal session. Without this
  // guard, clicking ACTIVE (which clears selectedOption) re-fires this effect
  // and the bench fallback silently overwrites the user's intent. The ref
  // resets on modal close so the next open auto-selects fresh.
  useEffect(() => {
    if (isVisible && player && !selectedOption && !userPickedSlotRef.current) {
      const { available, benchOption, minorsOption } = analyzePositionAvailability;

      let bestOption = null;
      const playerPosition = player?.info?.position;
      const directMatch = available.find(opt => opt.position === playerPosition);

      if (directMatch) {
        bestOption = directMatch.slotId;
      } else if (available.length > 0) {
        bestOption = available[0].slotId;
      } else if (benchOption?.canAssign) {
        bestOption = benchOption.slotId;
      } else if (minorsOption?.canAssign) {
        bestOption = minorsOption.slotId;
      }

      if (bestOption) {
        setSelectedOption(bestOption);
      }
    }

    if (!isVisible) {
      setSelectedOption('');
      setError('');
      setShowAlternatives(false);
      // Bug 1 fix: reset user-intent so next open re-runs auto-select fresh.
      userPickedSlotRef.current = false;
    }
  }, [isVisible, player, analyzePositionAvailability, selectedOption]);

  // DD-DL-DEADLOCK-0726 — ACTIVATE-WHEN-FULL (Bible §4.5.15.1). Mobile is the surface an owner is
  // most likely to hit this on. Same shared service as desktop; §14.0 keeps the COMPONENTS
  // separate, not the services.
  const flaggedPosition = player?.league_context?.position || player?.info?.position;
  const positionRequirements = league?.roster_settings?.position_requirements;
  // CANONICAL eligibility (Bible §4.5.17 Layer 1) — the same helper this component already uses
  // for slot availability. A bare position string would miss composite-slot expansion and
  // auto-earned eligibility, so a player who has earned OF would be offered the wrong teammates.
  const flaggedEligibility = getEffectiveEligibility(player) || null;
  const showDisplaceChooser = (
    mode === 'move' && currentStatus === 'dl' && selectedStatus === 'active'
    && needsActivateWhenFull(currentRoster, flaggedPosition, positionRequirements, flaggedEligibility)
  );
  const slotMates = showDisplaceChooser
    ? eligibleSlotMates(currentRoster, flaggedPosition, flaggedEligibility)
    : [];

  const handleAssignment = async () => {
    if (!selectedOption) {
      setError('Please select a position');
      return;
    }

    setError('');

    // Check if assignment requires a warning (DL or Minors)
    const requiresWarning = selectedStatus === 'dl' || selectedStatus === 'minors';
    
    if (requiresWarning) {
      // Store the assignment and show warning
      setPendingAssignment({
        assignment: selectedOption,
        roster_status: selectedStatus,
        roster_position: selectedStatus === 'active' ? selectedOption : null
      });
      setShowWarning(true);
      return;
    }

    if (showDisplaceChooser && !displaceChoice) {
      setError('Your active roster is full. Choose a teammate to move to the bench.');
      return;
    }

    // Proceed with assignment for active/bench
    try {
      await onAssign({
        player,
        assignment: selectedOption,
        roster_status: selectedStatus,
        roster_position: displaceChoice
          ? displaceChoice.slot
          : (selectedStatus === 'active' ? selectedOption : null),
        displace_league_player_id: displaceChoice ? displaceChoice.leaguePlayerId : undefined,
        mode: mode
      });
    } catch (err) {
      setError(formatApiError(err, 'Failed to assign position'));
    }
  };

  const handleConfirmWarning = async () => {
    if (!pendingAssignment) return;

    setShowWarning(false);
    
    try {
      await onAssign({
        player,
        assignment: pendingAssignment.assignment,
        roster_status: pendingAssignment.roster_status,
        roster_position: pendingAssignment.roster_position,
        mode: mode
      });
    } catch (err) {
      setError(err.message || 'Failed to assign position');
    } finally {
      setPendingAssignment(null);
    }
  };

  const handleCancelWarning = () => {
    setShowWarning(false);
    setPendingAssignment(null);
  };

  // Get warning message based on assignment type
  const getWarningMessage = () => {
    if (pendingAssignment?.roster_status === 'dl') {
      return 'In accordance with league rules, this player must be on the MLB IL. Do you wish to continue?';
    } else if (pendingAssignment?.roster_status === 'minors') {
      return 'In accordance with league rules, this player must be in the minor leagues. Do you wish to continue?';
    }
    return '';
  };

  // Render individual position option
  const renderPositionOption = (option, isAlternative = false) => {
    const { position, availableSlots, maxSlots, currentlyFilled, canAssign, slotId } = option;
    
    const getPositionIcon = (pos) => {
      if (pos === 'bench') return Users;
      if (pos === 'minors') return Shield;
      if (pos === 'dl') return Heart;
      return Activity;
    };
    
    const Icon = getPositionIcon(position);
    const isSelected = selectedOption === slotId;
    
    return (
      <div
        key={slotId || position}
        onClick={() => {
          if (canAssign) {
            userPickedSlotRef.current = true;
            setSelectedOption(slotId);
          }
        }}
        className={`
          rounded-lg border-2 cursor-pointer transition-all duration-200
          ${isSelected 
            ? `${dynastyTheme.classes.border.primaryBright} bg-yellow-400/10` 
            : canAssign 
              ? `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5`
              : `${dynastyTheme.classes.border.neutral} opacity-50 cursor-not-allowed bg-neutral-800/20`
          }
          ${isAlternative ? 'border-amber-500/30 bg-amber-500/5' : ''}
        `}
        style={{ padding: '9px' }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center" style={{ gap: '9px' }}>
            <Icon className={
              canAssign 
                ? dynastyTheme.classes.text.primary 
                : dynastyTheme.classes.text.neutralDark
            } style={{ width: '12px', height: '12px' }} />
            <div>
              <span className={`font-medium ${
                canAssign 
                  ? dynastyTheme.classes.text.white 
                  : dynastyTheme.classes.text.neutralDark
              }`} style={{ fontSize: '10.5px' }}>
                {position === 'bench' ? 'Bench' :
                 position === 'minors' ? 'Minor Leagues' :
                 position.toUpperCase()}
              </span>
              {position === 'minors' && (
                <div className={dynastyTheme.classes.text.warning} style={{ fontSize: '9px', marginTop: '3px' }}>
                  No contract until called up
                </div>
              )}
            </div>
          </div>
          
          <div className="text-right">
            <div className={
              canAssign 
                ? dynastyTheme.classes.text.neutralLight 
                : dynastyTheme.classes.text.neutralDark
            } style={{ fontSize: '9px' }}>
              {currentlyFilled}/{maxSlots} slots
            </div>
            {canAssign ? (
              <div className={`${dynastyTheme.classes.text.success} font-medium`} style={{ fontSize: '9px' }}>
                {availableSlots} available
              </div>
            ) : (
              <div className={`${dynastyTheme.classes.text.error} font-medium`} style={{ fontSize: '9px' }}>
                Full
              </div>
            )}
          </div>
        </div>
        
        {isSelected && (
          <div className="border-t" style={{ marginTop: '6px', paddingTop: '6px', borderColor: 'var(--team-primary-highlight)' }}>
            <div className="flex items-center justify-between">
              <div className={`${dynastyTheme.classes.text.primary} flex items-center`} style={{ fontSize: '9px', gap: '3px' }}>
                <CheckCircle style={{ width: '9px', height: '9px' }} />
                Selected
              </div>
              {!isAlternative && (
                <span className={`rounded ${dynastyTheme.classes.bg.success} ${dynastyTheme.classes.text.black} font-medium`} style={{ fontSize: '9px', padding: '1.5px 6px' }}>
                  Recommended
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render player eligibility info
  const renderEligibilityInfo = () => {
    // Bible § 4.5.9: canonical eligibility only. No fallback to [primary] —
    // skips composite expansion + auto-earn carry-over.
    const eligiblePositions = player?.league_context?.roster?.effective_eligibility || [];
    
    return (
      <div className={`${dynastyTheme.classes.bg.darkLighter} rounded-lg`} style={{ padding: '9px', marginBottom: '12px' }}>
        <div className="flex items-center" style={{ gap: '6px', marginBottom: '6px' }}>
          <Crown className={dynastyTheme.classes.text.primary} style={{ width: '12px', height: '12px' }} />
          <span className={`font-medium ${dynastyTheme.classes.text.white}`} style={{ fontSize: '10.5px' }}>
            {player?.info?.first_name} {player?.info?.last_name}
          </span>
          <span className={dynastyTheme.classes.text.neutralLight} style={{ fontSize: '9px' }}>
            {player?.info?.mlb_team}
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: '3px' }}>
          <span className={dynastyTheme.classes.text.neutralLight} style={{ fontSize: '9px', marginRight: '6px' }}>Eligible:</span>
          {eligiblePositions.map(pos => (
            <span 
              key={pos}
              className={`${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} rounded font-medium`}
              style={{ fontSize: '9px', padding: '3px 6px' }}
            >
              {pos}
            </span>
          ))}
        </div>
      </div>
    );
  };

  if (!isVisible) return null;

  // DD-FIXES-0705 Fix 2 fail-loud guard (Rule 9): a missing or malformed
  // league shape is a CALLER BUG and must present as one — never as a roster
  // condition. Pre-fix, a flat league prop (no roster_settings nesting) fell
  // through analyzePositionAvailability's shape guard into available:[] and
  // rendered a FALSE "All Active Positions Full" with every action disabled
  // (Bug 2, BUG_2_MODAL_ADD_INVESTIGATION_20260705.md). Callers pass the
  // canonical §5 league object (useAssignmentLeagueConfig). No fallback, no
  // reshaping, no dual-shape acceptance.
  if (!league?.roster_settings?.position_requirements) {
    console.error(
      'MobilePositionAssignmentModal: league prop is missing roster_settings.position_requirements — ',
      'caller must pass the canonical league shape (Bible §5) via useAssignmentLeagueConfig.',
      league
    );
    return (
      <div className="fixed inset-0 flex items-end justify-center" style={{ zIndex: 999999, backgroundColor: 'rgba(0,0,0,0.6)' }}>
        <div className={`${dynastyTheme.components.card.base} w-full rounded-t-2xl`} style={{ padding: '20px' }}>
          <div className="flex items-center" style={{ gap: '8px', marginBottom: '10px' }}>
            <AlertTriangle className={dynastyTheme.classes.text.error} style={{ width: '18px', height: '18px' }} />
            <span className={`font-medium ${dynastyTheme.classes.text.error}`} style={{ fontSize: '13px' }}>
              League configuration unavailable
            </span>
          </div>
          <p className={dynastyTheme.classes.text.neutralLight} style={{ fontSize: '11.5px', marginBottom: '14px' }}>
            Position availability can't be computed because the league's roster
            configuration didn't load. This is an app error — not a full roster.
            Close and try again; if it persists, report it.
          </p>
          <button
            onClick={onCancel}
            className={`${dynastyTheme.components.button.secondary} w-full`}
            style={{ padding: '10px', fontSize: '12px' }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const { available, benchOption, minorsOption } = analyzePositionAvailability;
  // Only check active positions for "All Positions Full" warning
  const hasAvailableActiveOptions = available.length > 0;
  
  // Match desktop behavior: show buttons if slots exist (like desktop), but check availability for enabling
  // Desktop shows: benchOption && benchOption.maxSlots > 0
  // Desktop shows: league?.roster_settings?.dl_slots > 0
  // Desktop shows: minorsOption && minorsOption.maxSlots > 0
  const showBenchButton = benchOption && benchOption.maxSlots > 0;
  const showDLButton = league?.roster_settings?.dl_slots > 0;
  const showMinorsButton = minorsOption && minorsOption.maxSlots > 0;
  
  // But check availability for enabling the button (canAssign checks if there are available slots)
  const hasAvailableBench = benchOption?.canAssign === true;
  const hasAvailableDL = showDLButton && (() => {
    const dlCount = Array.isArray(currentRoster) 
      ? currentRoster.filter(p => {
          const status = p.league_context?.roster?.status || p.roster?.status;
          return status === 'dl';
        }).length 
      : 0;
    return dlCount < (league?.roster_settings?.dl_slots || 0);
  })();
  const hasAvailableMinors = minorsOption?.canAssign === true;
  

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/70"
        style={{ zIndex: 999999 }}
        onClick={handleBackdropClick}
      >
        <div className="relative flex items-center justify-center min-h-screen" style={{ padding: '12px' }}>
          <div
            className={`${dynastyTheme.components.card.base} flex flex-col`}
            style={{
              width: '100%',
              maxWidth: '324px', // 75% of original (432px * 0.75)
              maxHeight: '67.5vh', // 75% of original (90vh * 0.75)
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[600]}`,
              boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.25)}`,
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '13.5px' }}>
              {/* Header */}
              <div className="flex items-center justify-between" style={{ marginBottom: '13.5px' }}>
                <h2 className={`font-bold ${dynastyTheme.classes.text.white} mb-0`} style={{ fontSize: '12px' }}>
                  Assign Roster Position
                </h2>
                <button
                  onClick={onCancel}
                  className={`${dynastyTheme.classes.text.neutralLight} hover:${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.neutral} transition-colors rounded-lg`}
                  style={{ padding: '3px' }}
                >
                  <X style={{ width: '15px', height: '15px' }} />
                </button>
              </div>

              {/* Player Eligibility Info */}
              {renderEligibilityInfo()}

              {/* Roster Status Selection */}
              <div style={{ marginBottom: '13.5px' }}>
                <h3 className={`font-medium ${dynastyTheme.classes.text.white}`} style={{ fontSize: '10.5px', marginBottom: '9px' }}>
                  Roster Status
                </h3>
                {/*
                  Phase 2.3: grid widens to fit the 5th "Drop a player"
                  button when it renders (active full + bench/DL/minors
                  has room). Per ROSTER_PLAN_SPEC.md § B.5.1 + § B.5.7.
                  Mobile mirrors desktop's grid-cols-2 / sm:grid-cols-5
                  responsive pattern; on phone widths the 5th button
                  wraps to a second row.
                */}
                <div className="grid grid-cols-2 sm:grid-cols-5" style={{ gap: '6px' }}>
              <button
                onClick={() => {
                  userPickedSlotRef.current = true;
                  setSelectedStatus('active');
                  setSelectedOption('');
                }}
                className={`
                  rounded-lg border-2 transition-all duration-200
                  ${selectedStatus === 'active'
                    ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                    : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                  }
                `}
                style={{ padding: '9px' }}
              >
                <div className="flex flex-col items-center" style={{ gap: '6px' }}>
                  <Activity className={selectedStatus === 'active' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight} style={{ width: '15px', height: '15px' }} />
                  <span className={`font-medium ${selectedStatus === 'active' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`} style={{ fontSize: '9px' }}>
                    Active
                  </span>
                </div>
              </button>

              {showBenchButton && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('bench');
                    setSelectedOption('bench');
                  }}
                  className={`
                    rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'bench'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                  style={{ padding: '9px' }}
                >
                  <div className="flex flex-col items-center" style={{ gap: '6px' }}>
                    <Users className={selectedStatus === 'bench' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight} style={{ width: '15px', height: '15px' }} />
                    <span className={`font-medium ${selectedStatus === 'bench' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`} style={{ fontSize: '9px' }}>
                      Bench
                    </span>
                  </div>
                </button>
              )}

              {showDLButton && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('dl');
                    setSelectedOption('dl');
                  }}
                  className={`
                    rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'dl'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                  style={{ padding: '9px' }}
                >
                  <div className="flex flex-col items-center" style={{ gap: '6px' }}>
                    <Heart className={selectedStatus === 'dl' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight} style={{ width: '15px', height: '15px' }} />
                    <span className={`font-medium ${selectedStatus === 'dl' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`} style={{ fontSize: '9px' }}>
                      DL
                    </span>
                  </div>
                </button>
              )}

              {showMinorsButton && (
                <button
                  onClick={() => {
                    userPickedSlotRef.current = true;
                    setSelectedStatus('minors');
                    setSelectedOption('minors');
                  }}
                  className={`
                    rounded-lg border-2 transition-all duration-200
                    ${selectedStatus === 'minors'
                      ? `${dynastyTheme.classes.border.primaryBright} ${dynastyTheme.classes.bg.primary}/10`
                      : `${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50`
                    }
                  `}
                  style={{ padding: '9px' }}
                >
                  <div className="flex flex-col items-center" style={{ gap: '6px' }}>
                    <Shield className={selectedStatus === 'minors' ? dynastyTheme.classes.text.primary : dynastyTheme.classes.text.neutralLight} style={{ width: '15px', height: '15px' }} />
                    <span className={`font-medium ${selectedStatus === 'minors' ? dynastyTheme.classes.text.white : dynastyTheme.classes.text.neutralLight}`} style={{ fontSize: '9px' }}>
                      Minors
                    </span>
                  </div>
                </button>
              )}

              {/*
                Phase 2.3: 5th button — Drop a player. Per
                ROSTER_PLAN_SPEC.md § B.5.1 + § B.5.7. Renders only
                when:
                  - active slots are full (available.length === 0), AND
                  - bench/DL/minors has room (any of hasAvailable*), AND
                  - parent provided onOpenDropShuffle callback.
                Visual: action-style with ArrowLeftRight icon to
                differentiate from the four status destinations.
                Mobile sizing: padding 9px, icon 15x15, text 9px —
                mirrors existing peer button sizing.
              */}
              {available.length === 0
                && (hasAvailableBench || hasAvailableDL || hasAvailableMinors)
                && onOpenDropShuffle && (
                <button
                  onClick={onOpenDropShuffle}
                  className={`rounded-lg border-2 transition-all duration-200 ${dynastyTheme.classes.border.neutral} hover:border-yellow-400/50 hover:bg-yellow-400/5`}
                  style={{ padding: '9px' }}
                  title="Drop a roster player to make room for this player"
                >
                  <div className="flex flex-col items-center" style={{ gap: '6px' }}>
                    <ArrowLeftRight className={dynastyTheme.classes.text.primary} style={{ width: '15px', height: '15px' }} />
                    <span className={`font-medium ${dynastyTheme.classes.text.white}`} style={{ fontSize: '9px' }}>
                      Drop a player
                    </span>
                  </div>
                </button>
              )}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className={`${dynastyTheme.components.badge.error} rounded-lg`} style={{ padding: '9px', marginBottom: '12px' }}>
              <div className="flex items-center" style={{ gap: '6px' }}>
                <AlertTriangle className={dynastyTheme.classes.text.error} style={{ width: '12px', height: '12px' }} />
                  <span className={dynastyTheme.classes.text.error} style={{ fontSize: '10.5px' }}>{error}</span>
                </div>
              </div>
              )}

              {/* No Available Active Options — only show for active status.
                  Bug 1 fix (2026-05-15): when onOpenDropShuffle is wired,
                  promote a prominent "Drop a Player to Make Room" CTA inside
                  this panel so the user has a clear next action instead of
                  hunting for the small 5th button in the status grid. */}
              {selectedStatus === 'active' && !hasAvailableActiveOptions && (
                <div className={`${dynastyTheme.components.badge.warning} rounded-lg`} style={{ padding: '12px', marginBottom: '12px' }}>
                  <div className="flex items-center" style={{ gap: '6px', marginBottom: '6px' }}>
                    <AlertTriangle className={dynastyTheme.classes.text.warning} style={{ width: '15px', height: '15px' }} />
                    <span className={`font-medium ${dynastyTheme.classes.text.warning}`} style={{ fontSize: '10.5px' }}>All Active Positions Full</span>
                  </div>
                  <p className={dynastyTheme.classes.text.warning} style={{ fontSize: '10.5px', marginBottom: '9px' }}>
                    All eligible active positions are currently filled. Consider:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '10.5px' }}>• Using bench, DL, or minors slots</div>
                    <div style={{ fontSize: '10.5px' }}>• Dropping another player to make room</div>
                    <div style={{ fontSize: '10.5px' }}>• Trading players to create space</div>
                  </div>
                  {onOpenDropShuffle && (hasAvailableBench || hasAvailableDL || hasAvailableMinors) && (
                    <button
                      onClick={onOpenDropShuffle}
                      className={`w-full ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} rounded-lg font-semibold transition-colors hover:opacity-90`}
                      style={{ padding: '10px', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <ArrowLeftRight style={{ width: '14px', height: '14px' }} />
                      Drop a Player to Make Room
                    </button>
                  )}
                </div>
              )}

              {/* Available Position Options (only show if status is 'active') */}
              {selectedStatus === 'active' && available.length > 0 && (
                <div style={{ marginBottom: '18px' }}>
              <h3 className={`font-medium ${dynastyTheme.classes.text.white} flex items-center`} style={{ fontSize: '10.5px', marginBottom: '9px', gap: '6px' }}>
                <CheckCircle className={dynastyTheme.classes.text.success} style={{ width: '12px', height: '12px' }} />
                Available Active Lineup Positions
              </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {available.map(option => renderPositionOption(option))}
                </div>
              </div>
              )}

              {/* Show Alternatives Button (only for active status) */}
              {selectedStatus === 'active' && analyzePositionAvailability.alternatives && analyzePositionAvailability.alternatives.length > 0 && !showAlternatives && (
                <button
              onClick={() => setShowAlternatives(true)}
              className={`w-full ${dynastyTheme.classes.bg.darkLighter} rounded-lg border-2 border-amber-500/30 hover:bg-amber-500/10 ${dynastyTheme.classes.transition}`}
              style={{ padding: '9px', marginBottom: '12px' }}
            >
              <div className="flex items-center justify-center" style={{ gap: '6px' }}>
                <AlertTriangle className={dynastyTheme.classes.text.warning} style={{ width: '12px', height: '12px' }} />
                <span className={`${dynastyTheme.classes.text.warning} font-medium`} style={{ fontSize: '9px' }}>
                  View Full Positions ({analyzePositionAvailability.alternatives.length} currently full)
                </span>
                  <ChevronDown className={dynastyTheme.classes.text.warning} style={{ width: '12px', height: '12px' }} />
                </div>
              </button>
              )}

              {/* Full Positions (Alternatives) - only for active status */}
              {selectedStatus === 'active' && showAlternatives && analyzePositionAvailability.alternatives && analyzePositionAvailability.alternatives.length > 0 && (
                <div style={{ marginBottom: '18px' }}>
              <h3 className={`font-medium ${dynastyTheme.classes.text.warning} flex items-center`} style={{ fontSize: '10.5px', marginBottom: '9px', gap: '6px' }}>
                <AlertTriangle className={dynastyTheme.classes.text.warning} style={{ width: '12px', height: '12px' }} />
                Currently Full Positions
                <button
                  onClick={() => setShowAlternatives(false)}
                  className={`ml-auto ${dynastyTheme.classes.text.neutralLight} hover:text-white`}
                  style={{ fontSize: '9px' }}
                >
                  Hide
                </button>
              </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {analyzePositionAvailability.alternatives.map(option => renderPositionOption(option, true))}
                </div>
                <div className={dynastyTheme.classes.bg.darkLighter} style={{ marginTop: '9px', padding: '9px', borderRadius: '6px' }}>
                  <p className={dynastyTheme.classes.text.warning} style={{ fontSize: '9px' }}>
                    These positions are full. You would need to move another player first.
                  </p>
                </div>
              </div>
              )}

              {/* DD-DL-DEADLOCK-0726 — ACTIVATE-WHEN-FULL chooser (§14.0 parity with desktop). */}
              {showDisplaceChooser && (
                <div data-testid="displace-chooser" style={{ marginBottom: '10px' }}>
                  <div className={`${dynastyTheme.classes.text.white}`}
                       style={{ fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                    Your active roster is full
                  </div>
                  <div className={`${dynastyTheme.classes.text.neutralLight}`}
                       style={{ fontSize: '10px', marginBottom: '6px' }}>
                    Choose a teammate to move to the bench — he and {player?.info?.first_name}{' '}
                    swap in one move. Nobody is dropped.
                  </div>
                  {slotMates.length === 0 ? (
                    <div className={`${dynastyTheme.classes.text.warning}`} style={{ fontSize: '10px' }}>
                      No teammate holds a slot this player is eligible for.
                    </div>
                  ) : slotMates.map((m) => (
                    <button
                      key={m.leaguePlayerId}
                      type="button"
                      data-testid={`displace-option-${m.slot}`}
                      onClick={() => {
                        // Choosing a slot-mate IS the slot selection (§14.0 parity with desktop).
                        setDisplaceChoice(m);
                        setSelectedOption(m.slot);
                        setError('');
                      }}
                      className={`w-full text-left rounded-lg ${
                        displaceChoice?.leaguePlayerId === m.leaguePlayerId
                          ? `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black}`
                          : `${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white}`
                      }`}
                      style={{ padding: '6px 8px', fontSize: '10.5px', marginBottom: '4px' }}
                    >
                      {m.name} <span style={{ opacity: 0.75 }}>{m.slot}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex" style={{ gap: '9px' }}>
            <button
              onClick={onCancel}
              className={`${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} hover:${dynastyTheme.classes.bg.darkLighter} transition-colors rounded-lg`}
              style={{ padding: '6px 12px', fontSize: '10.5px', flex: 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleAssignment}
              disabled={
                !selectedOption || 
                (selectedStatus === 'active' && !hasAvailableActiveOptions) ||
                (selectedStatus === 'bench' && !hasAvailableBench) ||
                (selectedStatus === 'dl' && !hasAvailableDL) ||
                (selectedStatus === 'minors' && !hasAvailableMinors)
              }
              className={`
                ${
                  !selectedOption || 
                  (selectedStatus === 'active' && !hasAvailableActiveOptions) ||
                  (selectedStatus === 'bench' && !hasAvailableBench) ||
                  (selectedStatus === 'dl' && !hasAvailableDL) ||
                  (selectedStatus === 'minors' && !hasAvailableMinors)
                    ? 'bg-neutral-700/50 text-neutral-500 cursor-not-allowed rounded-md' 
                    : `${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} hover:opacity-90 transition-colors rounded-lg`
                }
              `}
              style={{ padding: '6px 12px', fontSize: '10.5px', flex: 1, fontWeight: '600' }}
            >
                {selectedOption ? 'Assign Player' : 'Select Position'}
              </button>
            </div>

            {/* Helpful Tips */}
            <div className={dynastyTheme.classes.bg.darkLighter} style={{ marginTop: '18px', padding: '12px', borderRadius: '6px' }}>
              <h4 className={`font-medium ${dynastyTheme.classes.text.white}`} style={{ fontSize: '10.5px', marginBottom: '6px' }}>Tips:</h4>
              <div className={dynastyTheme.classes.text.neutralLight} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                <div style={{ fontSize: '9px' }}>• Active lineup players count against salary cap</div>
                <div style={{ fontSize: '9px' }}>• Bench players count against salary cap</div>
                <div style={{ fontSize: '9px' }}>• Minor league players have no contract (no salary)</div>
                {league?.roster_settings?.dl_slots > 0 && <div style={{ fontSize: '9px' }}>• DL players count against salary cap but don't play</div>}
                <div style={{ fontSize: '9px' }}>• You can move players between positions later</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Warning Dialog for DL/Minors */}
      {showWarning && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center"
          style={{ zIndex: 9999999 }}
          onClick={handleCancelWarning}
        >
          <div
            className={`${dynastyTheme.components.card.base} flex flex-col`}
            style={{
              width: '90%',
              maxWidth: '300px',
              border: `1px solid ${dynastyTheme.tokens.colors.warning}`,
              boxShadow: `0 25px 50px -12px ${withAlpha(C.black, 0.5)}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '18px' }}>
              <div className="flex items-center" style={{ gap: '9px', marginBottom: '12px' }}>
                <AlertTriangle className={dynastyTheme.classes.text.warning} style={{ width: '18px', height: '18px' }} />
                <h3 className={`font-bold ${dynastyTheme.classes.text.white}`} style={{ fontSize: '13.5px' }}>
                  League Rule Warning
                </h3>
              </div>
              
              <p className={dynastyTheme.classes.text.neutralLight} style={{ fontSize: '11px', marginBottom: '18px', lineHeight: '1.5' }}>
                {getWarningMessage()}
              </p>

              <div className="flex" style={{ gap: '9px' }}>
                <button
                  onClick={handleCancelWarning}
                  className={`${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.white} hover:opacity-80 transition-opacity rounded-lg`}
                  style={{ padding: '9px 18px', fontSize: '11px', flex: 1, fontWeight: '600' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmWarning}
                  className={`${dynastyTheme.classes.bg.warning} ${dynastyTheme.classes.text.black} hover:opacity-90 transition-opacity rounded-lg`}
                  style={{ padding: '9px 18px', fontSize: '11px', flex: 1, fontWeight: '600' }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobilePositionAssignmentModal;
